import { fetchProducts, generateOpt } from '../api/s1Api';
import { Product } from '../interfaces/S1';
import { validateRedirectUrl } from '../shared/helpers/securityHelpers';
import { OrganizationContext } from './OrganizationContext';
import React, { createContext, useContext, useEffect, useState } from 'react';

interface ProductsContextProps {
	products: Product[] | null;
	switchProduct: (product: Product) => void;
}

export const ProductsContext = createContext<ProductsContextProps>({
	products: null,
	switchProduct: () => null,
});

const S1_DEV_URL = 'https://dev-s1.dev.sovos.org/gateway';
const S1_QA_URL = 'https://qa-s1.dev.sovos.org/gateway';
const S1_INT_URL = 'https://int-s1.dev.sovos.org/gateway';
const S1_STG_URL = 'https://stg-s1.sovos.com/gateway';
const S1_UAT_URL = 'https://uat-s1.sovos.com/gateway';
const S1_PRD_URL = 'https://s1.sovos.com/gateway';

export const ProductsContextProvider = ({ children }: { children: React.ReactNode }) => {
	const [products, setProducts] = useState<Product[] | null>(null);
	const { selectedOrg } = useContext(OrganizationContext);

	const getS1GatewayProduct = () => {
		let url;
		const currentUrl = window.location.href;

		if (currentUrl.includes('qa')) {
			url = S1_QA_URL;
		} else if (currentUrl.includes('int')) {
			url = S1_INT_URL;
		} else if (currentUrl.includes('dev') || currentUrl.includes('localhost')) {
			url = S1_DEV_URL;
		} else if (currentUrl.includes('stg')) {
			url = S1_STG_URL;
		} else if (currentUrl.includes('uat')) {
			url = S1_UAT_URL;
		} else {
			url = S1_PRD_URL;
		}

		return { name: 'S1 Gateway', url, isInternal: false };
	};

	const switchProduct = (product: Product) => {
		generateOpt().then(opt => {
			// Use same allowlist as securityHelpers (CWE-601); blocks redirect if URL not allowed
			const allowedUrl = validateRedirectUrl(product.url);
			if (allowedUrl !== product.url) {
				console.warn('Product URL not in allowlist');
				return;
			}
			try {
				const url = new URL(product.url);
				const params = new URLSearchParams(url.search);
				params.set('otp', opt);
				const org = sessionStorage.getItem('org');
				if (org) {
					params.set('org', org);
				}
				url.search = params.toString();
				window.location.href = url.toString();
			} catch {
				console.warn('Invalid product URL');
			}
		});
	};

	useEffect(() => {
		if (selectedOrg) {
			setProducts(null);
			fetchProducts(selectedOrg.id).then((response: Product[]) => {
				setProducts([
					getS1GatewayProduct(),
					...response
						.filter((product: Product) => !product.isInternal)
						.sort((a: Product, b: Product) => a.name.localeCompare(b.name)),
				]);
			});
		}
	}, [selectedOrg]);

	return <ProductsContext.Provider value={{ products, switchProduct }}>{children}</ProductsContext.Provider>;
};
